import { type ReactNode } from "react";
import BulkATCPopupContext from "../../BulkATCPopup/components/BulkATCPopupContext";
import { Permission, userHasPermission } from "../../utils/permissions";
import { useTranslation, type WithTranslation } from "react-i18next";
import { type MenuProps as MUIMenuProps, SimpleMenu, withNavigationMenuStyle } from "../../common/components/Menu/Menu";
import { MenuItem } from "../../common/components/Menu/MenuItem";
import { MENU_POS_RIGHT } from "../../common/components/Menu/MenuPositions";
import {
  AB_MONITOR_PAGE,
  CONTRACT_OVERVIEW_PAGE,
  ORDER_HISTORY_PAGE,
  QUOTE_HISTORY_PAGE,
} from "../../utils/url/staticUrls";

interface MenuProps extends MUIMenuProps {
  onClose: () => void;
  beforeOptionsChildren?: ReactNode;
  children?: never;
}

interface MenuEntry {
  permisisonCheck: () => boolean;
  factory: (t: WithTranslation["t"], closeMenu: () => void, openBulkPopup?: () => void) => JSX.Element;
}

export const entriesMenu: MenuEntry[] = [
  {
    permisisonCheck: () => userHasPermission(Permission.SEE_ABMONITOR),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={AB_MONITOR_PAGE}
        key={"abmonitor"}
        data-test-id={"header-btn-abmonitor"}
        onClick={closeMenu}
      >
        {t("header.link.ordersDropdown.abmonitor")}
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.SEARCH_ORDERS),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={ORDER_HISTORY_PAGE}
        key={"orderHistory"}
        data-test-id={"header-btn-order-history"}
        onClick={closeMenu}
      >
        {t("header.link.ordersDropdown.orders")}
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.SEARCH_QUOTES),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={QUOTE_HISTORY_PAGE}
        key={"searchQuotes"}
        data-test-id={"header-btn-quote-history"}
        onClick={closeMenu}
      >
        {t("header.link.quotes")}
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.BULK_UPLOAD),
    factory: (t, closeMenu, openBulkPopup) => (
      <MenuItem
        onClick={() => {
          openBulkPopup?.();
          closeMenu();
        }}
        key={"bulkAdd"}
        data-test-id={"header-btn-bulk-upload"}
      >
        {t("header.link.ordersDropdown.bulkAdd")}
      </MenuItem>
    ),
  },
  {
    permisisonCheck: () => userHasPermission(Permission.VIEW_CONTRACT),
    factory: (t, closeMenu) => (
      <MenuItem
        className={"title-medium"}
        // @ts-ignore
        component={"a"}
        href={CONTRACT_OVERVIEW_PAGE}
        key={"contractOverview"}
        data-test-id={"header-btn-contract-list"}
        onClick={closeMenu}
      >
        {t("header.link.ordersDropdown.contractOverview")}
      </MenuItem>
    ),
  },
];

const StyledMenu = withNavigationMenuStyle(SimpleMenu);

export const MyCompanyMenu = ({ onClose, beforeOptionsChildren, ...props }: MenuProps) => {
  const { t } = useTranslation();
  const visibleMenuEntries = entriesMenu.filter((item) => item.permisisonCheck());

  if (!visibleMenuEntries.length) {
    return null;
  }
  return (
    <BulkATCPopupContext>
      {({ openPopup }) => (
        <StyledMenu onClose={onClose} {...MENU_POS_RIGHT} {...props} disableAutoFocusItem={true}>
          {beforeOptionsChildren}
          {visibleMenuEntries.map((item) => item.factory(t, onClose, openPopup))}
        </StyledMenu>
      )}
    </BulkATCPopupContext>
  );
};
